// Code 128 barcodes: the values, and the bar and space widths to draw them. Shared by the
// screens (an SVG) and the server (a printer picture for the receipt printer).

// Bar and space widths for Code 128 values 0–106 (106 is the stop pattern).
export const C128 = (
  '212222 222122 222221 121223 121322 131222 122213 122312 132212 221213 221312 231212 112232 122132 122231 113222 123122 123221 223211 221132 ' +
  '221231 213212 223112 312131 311222 321122 321221 312212 322112 322211 212123 212321 232121 111323 131123 131321 112313 132113 132311 211313 ' +
  '231113 231311 112133 112331 132131 113123 113321 133121 313121 211331 231131 213113 213311 213131 311123 311321 331121 312113 312311 332111 ' +
  '314111 221411 431111 111224 111422 121124 121421 141122 141221 112214 112412 122114 122411 142112 142211 241211 221114 413111 241112 134111 ' +
  '111242 121142 121241 114212 124112 124211 411212 421112 421211 212141 214121 412121 111143 111341 131141 114113 114311 411113 411311 113141 ' +
  '114131 311141 411131 211412 211214 211232 2331112'
).split(' ');

/**
 * Values to draw: start code, data, checksum, stop. Digits-only text uses the compact number set
 * (two digits a bar pattern); an odd count (a 15-digit IMEI) switches to set B for the last digit.
 * Fewer, wider bars scan better, above all on a 203 dpi label printer like the Rollo.
 */
export function code128Values(text: string): number[] {
  const numeric = /^\d+$/.test(text) && text.length >= 4;
  const data: number[] = [];
  let start: number;
  if (numeric) {
    start = 105;
    const even = text.length - (text.length % 2);
    for (let i = 0; i < even; i += 2) data.push(Number(text.slice(i, i + 2)));
    // The odd digit out: "Code B" (100 in set C), then the digit in set B.
    if (even < text.length) data.push(100, text.charCodeAt(even) - 32);
  } else {
    start = 104;
    for (const ch of text) {
      const code = ch.charCodeAt(0);
      if (code < 32 || code > 126) throw new Error('Code 128 B only covers printable ASCII');
      data.push(code - 32);
    }
  }
  const check = data.reduce((sum, v, i) => sum + v * (i + 1), start) % 103;
  return [start, ...data, check, 106];
}


/** The bars (x and width, in modules) and the total width, quiet zones included (10 each side). */
export function code128Bars(text: string): { bars: { x: number; w: number }[]; total: number } {
  const widths = code128Values(text).map((v) => C128[v]).join('');
  const bars: { x: number; w: number }[] = [];
  let x = 10;
  [...widths].forEach((d, i) => {
    const w = Number(d);
    if (i % 2 === 0) bars.push({ x, w });
    x += w;
  });
  return { bars, total: x + 10 };
}
